import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import classes from '../../../pipeline.module.scss';
import Api from '../../../../../../../api/api';
import Table from '../../../../../../_common/Tables/Table';
import TableAndPagination from '../../../../../../_common/TableAndPagination/TableAndPagination';
import Modal from '../../../../../../_common/Modal/Modal';
import Loader from '../../../../../../_common/Loader/Loader';
import { Card } from '../../PipelineComponents';
import { STAR_ADHERENCE_THRESHOLD } from '../../../pipelineHelpers';

const title = 'Who to call';

const tableHeader = [
  { name: 'Member', path: 'name' },
  { name: 'Group', path: 'groupName' },
  { name: 'Medication', path: 'productName' },
  { name: 'PDC (group, year)', path: 'pdcPct' },
  { name: 'Days needed', path: 'daysNeededText' },
  { name: 'In groups', path: 'inGroupsText' },
];

const formattedMembers = (list = []) => list.map((d) => ({
  ...d,
  id: d.PatientId,
  name: [d.firstName, d.lastName].filter(Boolean).join(' '),
  pdcPct: d.pdc || d.pdc === 0 ? `${d.pdc}%` : '-',
  daysNeededText: d.daysNeeded?.toString(),
  inGroupsText: d.inGroups?.toString(),
}));

const modalInitialState = {
  open: false,
  firstLoad: true,
  loading: false,
  list: [],
  totalPage: 1,
  page: 1,
  limit: 15,
};

const WhoToCallCard = ({ params }) => {
  const navigate = useNavigate();

  const firstLoad = useRef(true);

  const [membersData, setMembersData] = useState([]);
  const [showSeeMore, showSeeMoreToggle] = useState(false);
  const [loading, loadingToggle] = useState(true);

  const [modal, setModal] = useState(modalInitialState);

  useEffect(() => {
    (async () => {
      try {
        loadingToggle(true);

        const { data } = await Api.getStarAdherence({ ...params, limit: 10, page: 1 });

        setMembersData(formattedMembers(data?.data?.response));

        showSeeMoreToggle(data?.data?._meta?.pageCount > 1);

        loadingToggle(false);
        firstLoad.current = false;
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [params]);

  useEffect(() => {
    if (!modal.open) return;

    (async () => {
      try {
        setModal((prev) => ({ ...prev, loading: true }));

        const { data } = await Api.getStarAdherence({
          ...params, page: modal.page, limit: modal.limit,
        });

        setModal((prev) => ({
          ...prev,
          list: formattedMembers(data?.data?.response),
          totalPage: data?.data?._meta?.pageCount || 1,
        }));

        setModal((prev) => ({ ...prev, loading: false, firstLoad: false }));
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [params, modal.open, modal.page, modal.limit]);

  const openModal = () => setModal({ ...modalInitialState, open: true });

  const onChangePageData = (path, value) => {
    setModal((prev) => (path === 'limit'
      ? { ...prev, limit: value, page: 1 }
      : { ...prev, page: value }));
  };

  return (
    <>
      <Card
        title={title}
        caption={`Members who can still reach ${STAR_ADHERENCE_THRESHOLD}% - easiest first`}
        loading={loading}
        firstLoad={firstLoad.current}
        isEmpty={!membersData.length}
      >
        <div className={classes.pipeline_table}>
          <Table
            header={tableHeader}
            data={membersData}
            className={classes.clickable_row}
            onClick={(PatientId) => navigate(`/patients/${PatientId}`)}
          />
        </div>

        {showSeeMore && (
          <div className={classes.see_more_wrapper}>
            <button
              type="button"
              className={classes.see_more_btn}
              onClick={openModal}
            >
              See more
            </button>
          </div>
        )}

        <p className={`${classes.note} ${classes.bordered_top}`}>
          Members who can no longer reach
          {' '}
          <b>{`${STAR_ADHERENCE_THRESHOLD}%`}</b>
          {' '}
          this year are kept off this list, so no calls are spent on cases that cannot move. Opening a member
          shows the individual fills behind the shortfall - section 01 reports
          {' '}
          <b>PDC (medication, period)</b>
          {' '}
          while this table reports
          {' '}
          <b>PDC (group, year)</b>
          , so the two numbers will not match for the same member.
        </p>
      </Card>

      <Modal
        open={modal.open}
        onClose={() => setModal((prev) => ({ ...prev, open: false }))}
        className={classes.members_modal}
      >
        <div className={classes.members_modal_content}>
          <h2>{title}</h2>

          <div className={classes.members_modal_table}>
            {modal.firstLoad
              ? (
                <div className={classes.members_modal_loader}>
                  <Loader />
                </div>
              )

              : (
                <TableAndPagination
                  header={tableHeader}
                  data={modal.list}
                  loading={modal.loading}
                  onChange={onChangePageData}
                  className={classes.clickable_row}
                  onClick={(PatientId) => navigate(`/patients/${PatientId}`)}
                  totalPage={modal.totalPage}
                  page={modal.page - 1}
                  limit={modal.limit}
                />
              )}
          </div>
        </div>
      </Modal>
    </>
  );
};

WhoToCallCard.propTypes = {
  params: PropTypes.object.isRequired,
};

export default WhoToCallCard;
